import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type PointerEvent,
} from "react";

import { Button } from "@/components/ui/button";

/**
 * Blacking out PII in the captured screenshot before it ever leaves the
 * browser (#319). Screens show customer names, phones and addresses, and a
 * screenshot is a copy of them — this is what lets a reporter cover one
 * without editing an image file themselves.
 *
 * A canvas rather than absolutely-positioned `<div>`s over an `<img>`: the
 * boxes have to survive being flattened into the one PNG the API stores, and
 * drawing them onto the same canvas that holds the picture is what makes
 * `flatten()` just a `toBlob()` call rather than a second render pass.
 */

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ScreenshotCanvasHandle {
  /** The image with every blackout box burned in. Null if nothing loaded. */
  flatten: (type: string, quality?: number) => Promise<Blob | null>;
  hasBoxes: () => boolean;
}

interface ScreenshotCanvasProps {
  screenshot: Blob;
}

export const ScreenshotCanvas = forwardRef<
  ScreenshotCanvasHandle,
  ScreenshotCanvasProps
>(function ScreenshotCanvas({ screenshot }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const boxesRef = useRef<Box[]>([]);
  const drawStartRef = useRef<{ x: number; y: number } | null>(null);
  const [boxCount, setBoxCount] = useState(0);

  function redraw() {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !image || !ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0);
    ctx.fillStyle = "#000000";
    for (const box of boxesRef.current) {
      ctx.fillRect(box.x, box.y, box.w, box.h);
    }
  }

  // One image per captured screenshot — a fresh blob each time the button is
  // clicked, never mutated in place.
  useEffect(() => {
    const url = URL.createObjectURL(screenshot);
    const image = new Image();
    image.onload = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = image.width;
        canvas.height = image.height;
      }
      imageRef.current = image;
      boxesRef.current = [];
      setBoxCount(0);
      redraw();
    };
    image.src = url;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [screenshot]);

  function pointFromEvent(e: PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    // The canvas is drawn at the capture's real pixel size and scaled down
    // to fit the dialog with CSS, so a pointer position in screen pixels has
    // to be rescaled back to canvas pixels before it means anything to boxes
    // burned into the image at full resolution.
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  function handlePointerDown(e: PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawStartRef.current = pointFromEvent(e);
  }

  function handlePointerMove(e: PointerEvent<HTMLCanvasElement>) {
    const start = drawStartRef.current;
    const ctx = canvasRef.current?.getContext("2d");
    if (!start || !ctx) return;

    const current = pointFromEvent(e);
    redraw();
    ctx.fillStyle = "#000000";
    ctx.fillRect(
      Math.min(start.x, current.x),
      Math.min(start.y, current.y),
      Math.abs(current.x - start.x),
      Math.abs(current.y - start.y),
    );
  }

  function handlePointerUp(e: PointerEvent<HTMLCanvasElement>) {
    const start = drawStartRef.current;
    drawStartRef.current = null;
    if (!start) return;

    const end = pointFromEvent(e);
    const box: Box = {
      x: Math.min(start.x, end.x),
      y: Math.min(start.y, end.y),
      w: Math.abs(end.x - start.x),
      h: Math.abs(end.y - start.y),
    };
    // A click that never dragged is not a box someone meant to draw.
    if (box.w > 2 && box.h > 2) {
      boxesRef.current = [...boxesRef.current, box];
      setBoxCount(boxesRef.current.length);
    }
    redraw();
  }

  useImperativeHandle(ref, () => ({
    flatten: (type, quality) =>
      new Promise((resolve) => {
        const canvas = canvasRef.current;
        if (!canvas) {
          resolve(null);
          return;
        }
        canvas.toBlob(
          (result) => {
            resolve(result);
          },
          type,
          quality,
        );
      }),
    hasBoxes: () => boxesRef.current.length > 0,
  }));

  return (
    <div className="flex flex-col gap-2">
      <div className="max-h-72 overflow-auto rounded-md border">
        <canvas
          ref={canvasRef}
          className="block w-full touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">
          Drag a box over anything that should stay private.
        </p>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          disabled={boxCount === 0}
          onClick={() => {
            boxesRef.current = [];
            setBoxCount(0);
            redraw();
          }}
        >
          Clear boxes
        </Button>
      </div>
    </div>
  );
});
